import { useMutation, useQuery } from '@apollo/client';
import { useAccount, useLogin } from '@bnnvara/accounts';
import { useEffect, useState } from 'react';

import { LIKE } from '../../api/mutations/like.gql';
import { GetUserLike } from '../../api/queries/me/likes.gql';

export function useUserLike({
  id,
  contentType,
  utm,
  fingerprint,
  brandId,
  interactionSlug,
}) {
  const { login, isLoggedIn, status } = useLogin();
  const {
    profile: { UID },
  } = useAccount();
  const [isLiked, setIsLiked] = useState(undefined);
  const { loading, error, data } = useQuery(GetUserLike, {
    variables: {
      contentId: id,
      contentType,
      userId: UID,
    },
    skip: !UID,
  });
  const [like] = useMutation(LIKE);

  useEffect(() => {
    if (status !== 'idle' && status !== 'success') {
      return;
    }

    if (isLoggedIn === false) {
      setIsLiked(false);
      return;
    }

    if (data?.me?.isLiked?.__typename === 'IsLikedErrorType') {
      return;
    }

    if (data?.me?.isLiked?.__typename === 'IsLikedSuccessType') {
      setIsLiked(data.me.isLiked.isLiked);
    }
  }, [data, status, isLoggedIn]);

  /**
   * This function returns:
   * - `null` when not logged in
   * - `true` when liked
   * - `false` when unliked
   */
  async function toggleLike() {
    if (!isLoggedIn) {
      login();

      return null;
    }

    if (isLiked === undefined) {
      return null;
    }

    let newValue = !isLiked;

    setIsLiked(newValue);

    const result = await like({
      variables: {
        contentId: id,
        contentType,
        isLiked: newValue,
        utm,
        fingerprint,
        brandId,
        slug: interactionSlug,
      },
    });

    if (result?.data?.like?.__typename === 'LikeSuccessResponseType') {
      newValue = result?.data?.like?.isLiked;
      setIsLiked(newValue);
      return newValue;
    }

    if (result?.data?.like?.__typename === 'LikeErrorResponseType') {
      newValue = !newValue;
      setIsLiked(newValue);
      return null;
    }

    return null;
  }

  return {
    isLiked,
    isReady: isLiked !== undefined && !loading && !error,
    toggleLike,
  };
}
